import { interpolatePath, serializeURLSearchParams } from "@/lib/utils";
import { QueryClient, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { z } from "zod";

export function createInvalidateQuery(endpoint: string) {
  return (
    client: QueryClient,
    customOptions?: {
      queryKeySuffix?: unknown[];
      pathParams?: Record<string, string | number>;
    },
  ) => {
    const { queryKeySuffix, pathParams = {} } = customOptions ?? {};
    const finalEndpoint = interpolatePath(endpoint, pathParams);

    client.invalidateQueries({
      queryKey: [finalEndpoint, ...(queryKeySuffix ?? [])],
    });
  };
}

export function createQueryHook<TResponse>(
  endpoint: string,
  paramsSchema: z.ZodTypeAny,
  defaultOptions?: Omit<UseQueryOptions<TResponse>, "queryKey">,
) {
  return (
    params: Partial<z.infer<typeof paramsSchema>> = {},
    customOptions?: {
      queryKeySuffix?: unknown[];
      pathParams?: Record<string, string | number>;
    },
    queryOptions?: Omit<UseQueryOptions<TResponse>, "queryKey">,
  ) => {
    const { queryKeySuffix, pathParams = {} } = customOptions ?? {};
    const finalEndpoint = interpolatePath(endpoint, pathParams);

    return useQuery<TResponse>({
      queryKey: [finalEndpoint, ...(queryKeySuffix ?? [params])],
      queryFn: async () => {
        const validatedParams = paramsSchema.parse(params);
        const queryString = serializeURLSearchParams(validatedParams);
        const url = `${finalEndpoint}${queryString ? `?${queryString}` : ""}`;

        const response = await fetch(url);

        if (response.status >= 400 && response.status < 500) {
          throw new Error("Client error");
        }

        return response.json();
      },

      placeholderData: (prev) => prev,
      ...defaultOptions,
      ...queryOptions,
    });
  };
}

export {
  useVehicleBrandsQuery,
  invalidateVehicleBrandsQuery,
} from "./queries/vehicle-brands";
export {
  useShippingSitesQuery,
  invalidateShippingSitesQuery,
} from "./queries/shipping-sites";
export {
  useBodyTypesQuery,
  invalidateBodyTypesQuery,
} from "./queries/body-types";
export {
  useNonVwfsLessorsQuery,
  invalidateNonVwfsLessorsQuery,
} from "./queries/non-vwfs-lessors";
export { useAvsKvpsQuery, invalidateAvsKvpsQuery } from "./queries/avs-kvps";
export {
  useAvsInternalAccountsQuery,
  invalidateAvsInternalAccountsQuery,
} from "./queries/avs-internal-accounts";
export {
  useRestrictedCathrinUsersQuery,
  invalidateRestrictedCathrinUsersQuery,
} from "./queries/restricted-cathrin-users";
export {
  useInternalAccountsQuery,
  invalidateInternalAccountsQuery,
} from "./queries/internal-accounts";
export {
  useActivityLogListQuery,
  invalidateActivityLogQuery,
} from "./queries/activity-log";
export { useAakRulesQuery, invalidateAakRulesQuery } from "./queries/aak-rules";

export {
  useColorRulesQuery,
  invalidateColorRulesQuery,
} from "./queries/color-rules";
export {
  useFacturationRulesQuery,
  invalidateFacturationRulesQuery,
} from "./queries/facturation-rules";
export {
  useLemaSalesPersonsQuery,
  invalidateLemaSalesPersonsQuery,
} from "./queries/lema-sales-persons";

export { useCatParticulierSearchMutation } from "./queries/cat/use-cat-particulier-search";
export { useCatSiretVerifyMutation } from "./queries/cat/use-cat-siret-verify";
